import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { EzLogo, NavMobileToggle, NavDesktopLinks } from "@/pages/LandingPage";
import {
  ShieldCheck,
  Mail,
  Ban,
  Monitor,
  Sparkles,
  ArrowLeft,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const RECRUITERS = [
  { name: "Micaela Giampaoletti", nickname: "Mica", email: "micaela.giampaoletti@ez.health" },
  { name: "Sarah Ahmed", nickname: null, email: "sarah.ahmed@ez.health" },
  { name: "Luz Ayus", nickname: null, email: "luz.ayus@ez.health" },
];

const SECTIONS = [
  {
    id: "emails",
    icon: <Mail className="w-6 h-6" />,
    title: "We always email from @ez.health",
    body: (
      <>
        <p className="text-slate-600 font-body leading-relaxed mb-5">
          Anyone reaching out to you on behalf of Ez Health will use an{" "}
          <span className="font-semibold text-ez-teal">@ez.health</span> email address.
          If the address ends in anything else — gmail.com, outlook.com, a random
          look-alike domain — assume it's not us.
        </p>
        <div className="bg-ez-surface rounded-2xl border border-slate-100 p-5">
          <p className="text-xs font-semibold text-ez-blue uppercase tracking-wider mb-3 font-body">
            Our main recruiters
          </p>
          <ul className="space-y-2.5">
            {RECRUITERS.map((r) => (
              <li
                key={r.email}
                className="flex items-center gap-2 text-sm font-body text-slate-700"
                data-testid={`recruiter-${r.email}`}
              >
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <span>
                  <span className="font-semibold text-ez-teal">{r.name}</span>
                  {r.nickname ? (
                    <span className="text-slate-500"> (“{r.nickname}”)</span>
                  ) : null}
                  <span className="text-slate-400"> — </span>
                  <span className="font-mono text-xs text-slate-600">{r.email}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <p className="text-slate-500 font-body text-sm leading-relaxed mt-4">
          We're growing fast, so lots of Ez Health employees pitch in on the early
          interviews — the names won't always be the three above. The one thing that
          will always be true: their email address ends in{" "}
          <span className="font-semibold text-ez-teal">@ez.health</span>.
        </p>
      </>
    ),
  },
  {
    id: "money",
    icon: <Ban className="w-6 h-6" />,
    title: "We will never ask you for money",
    body: (
      <>
        <p className="text-slate-600 font-body leading-relaxed">
          Not during the interview. Not as part of an offer. Not for equipment,
          training, certifications, background checks, or anything else.{" "}
          <span className="font-semibold text-ez-teal">Ever.</span>
        </p>
        <p className="text-slate-500 font-body text-sm leading-relaxed mt-4">
          If someone claiming to be from Ez Health asks you to send money, wire
          funds, buy gift cards, or pay a fee to “secure your role,” stop the
          conversation and report it to us at{" "}
          <a
            href="mailto:jobs@ez.health"
            className="text-ez-blue hover:underline font-semibold"
          >
            jobs@ez.health
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: "software",
    icon: <Monitor className="w-6 h-6" />,
    title: "Screen recording: ScreenCastify (or your own tool)",
    body: (
      <>
        <p className="text-slate-600 font-body leading-relaxed">
          A few interview steps ask you to record your screen so we can see how you
          approach a task. We recommend{" "}
          <a
            href="https://www.screencastify.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-ez-blue hover:underline font-semibold"
          >
            ScreenCastify
          </a>
          , a popular free Chrome extension — but you're welcome to use any
          screen-recording tool you already trust (QuickTime, Loom, OBS, etc.).
        </p>
        <div className="mt-5 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900 font-body leading-relaxed">
            <span className="font-semibold">We will never ask you to install any
            other software</span> — no remote-desktop apps, no “employee portal”
            downloads, no banking add-ons. If someone tells you to install
            something unfamiliar, that's a scam.
          </p>
        </div>
      </>
    ),
  },
  {
    id: "too-good",
    icon: <Sparkles className="w-6 h-6" />,
    title: "“Is this job too good to be true?”",
    body: (
      <>
        <p className="text-slate-600 font-body leading-relaxed mb-4">
          Some people might think so!
        </p>
        <p className="text-slate-600 font-body leading-relaxed mb-4">
          But talking to seniors on the phone, navigating the healthcare system and all the time not giving up or backing down is hard work — this is why we pay good money for it, and why Medicare reimburses this work for us. And Medicare reimburses it because when seniors get lost in the healthcare maze, they end up costing even more money, going to the nursing home, or even dying — all of these are outcomes that are cheaper to prevent than to treat.
        </p>
      </>
    ),
  },
];

export default function AvoidScams() {
  const navigate = useNavigate();
  useEffect(() => {
    document.title = "Avoid Scams | Ez Health";
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="avoid-scams-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 sm:gap-7">
            <NavMobileToggle />
            <a href="https://careers.ez.health" data-testid="scams-header-logo-link" aria-label="Ez Health careers home" className="inline-flex items-center">
              <EzLogo size="h-9" />
            </a>
            <NavDesktopLinks />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="scams-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-[#EDF5FB] to-white" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 lg:pt-20 lg:pb-16 text-center">
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, type: "spring", stiffness: 160, damping: 18 }}
            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-ez-blue/10 text-ez-blue mb-6"
          >
            <ShieldCheck className="w-8 h-8" />
          </motion.div>
          <motion.h1
            {...fadeUp}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-ez-teal font-heading leading-[1.1] mb-5"
            data-testid="scams-hero-title"
          >
            Avoid <span className="text-ez-blue">job scams</span>
          </motion.h1>
          <motion.p
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto font-body"
          >
            Remote-job scams targeting hourly and sales applicants have been on the
            rise. Before you reply to anyone claiming to be from Ez Health, take a
            minute to check what's real and what isn't.
          </motion.p>
        </div>
      </section>

      {/* Sections */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          {SECTIONS.map((s, i) => (
            <motion.div
              key={s.id}
              {...fadeUp}
              transition={{ duration: 0.45, delay: i * 0.05 }}
              className="flex gap-5 p-7 lg:p-8 bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`scams-section-${s.id}`}
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-ez-blue/10 flex items-center justify-center text-ez-blue">
                {s.icon}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl md:text-2xl font-bold text-ez-teal font-heading tracking-tight mb-3">
                  {s.title}
                </h2>
                <div>{s.body}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Reassurance / contact */}
      <section className="py-16 lg:py-20 bg-ez-surface border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-2xl md:text-3xl font-bold text-ez-teal font-heading tracking-tight mb-4">
              Not sure if a message is really from us? Have any other questions?
            </h2>
            <p className="text-base text-slate-600 font-body leading-relaxed mb-8 max-w-xl mx-auto">
              Forward suspicious emails or screenshots to{" "}
              <a
                href="mailto:jobs@ez.health"
                className="text-ez-blue hover:underline font-semibold"
              >
                jobs@ez.health
              </a>{" "}
              and we'll confirm whether it's legit. When in doubt, slow down —
              real opportunities will always wait for you to check.
            </p>
            <Button
              data-testid="scams-back-home-btn"
              onClick={() => navigate("/")}
              className="rounded-full bg-ez-blue text-white hover:bg-[#5A9BCF] px-8 py-3 font-semibold shadow-lg shadow-blue-200"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to home
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
